import { useMemo } from 'react';
import { Drawer, Descriptions, Empty } from 'antd';
import { isEmptyCell } from './formatters';

// Right-side slide-over panel triggered by a row click. The detail
// view is intentionally READ-ONLY — for editing, the grid still uses
// the Add/Edit/View form modal. This split mirrors how Linear, Notion
// and Stripe Dashboard handle row-preview: glance vs. edit.
//
// Caller supplies a `groups` definition:
//
//   [{
//     title: 'Customer Details',
//     items: [
//       { label: 'Code', dataIndex: 'code' },
//       { label: 'Name', dataIndex: 'name', render: (v) => <b>{v}</b> },
//       { label: 'GSTIN', dataIndex: 'gstin', render: (v) =>
//           <code className="erp-grid__code">{v}</code> }
//     ]
//   }, ...]
//
// `dataIndex` may be a string or array (mirrors AntD Table convention).
// `render` may return JSX. Empty values fall back to the muted dash.

const MUTED_DASH = <span className="erp-grid__muted">—</span>;

function readField(record, dataIndex) {
  if (!record || dataIndex === undefined || dataIndex === null) return undefined;
  if (Array.isArray(dataIndex)) {
    let cur = record;
    for (const k of dataIndex) {
      if (cur == null) return undefined;
      cur = cur[k];
    }
    return cur;
  }
  return record[dataIndex];
}

export default function ErpDataGridDetailDrawer({
  open,
  record,
  onClose,
  title,
  subtitle,
  width = 460,
  groups = [],
  footer = null
}) {
  const resolved = useMemo(() => {
    if (!record || !Array.isArray(groups)) return [];
    return groups.map((g, gi) => {
      const items = (g.items || []).map((it, ii) => {
        const di = it.dataIndex !== undefined ? it.dataIndex : it.key;
        const raw = readField(record, di);
        const out = typeof it.render === 'function' ? it.render(raw, record) : raw;
        const empty = isEmptyCell(out) && isEmptyCell(raw);
        return {
          key:
            it.key ||
            (Array.isArray(di) ? di.join('.') : di) ||
            `item-${gi}-${ii}`,
          label: it.label,
          span: it.span,
          children: empty ? MUTED_DASH : out
        };
      });
      return { ...g, key: g.key || `group-${gi}`, items };
    });
  }, [groups, record]);

  return (
    <Drawer
      open={open}
      placement="right"
      width={width}
      onClose={onClose}
      title={
        <div className="flex flex-col leading-tight">
          <span className="text-sm font-semibold text-ink-900 truncate">
            {title || 'Details'}
          </span>
          {subtitle ? (
            <span className="text-[11px] font-normal text-ink-500 truncate">
              {subtitle}
            </span>
          ) : null}
        </div>
      }
      destroyOnClose
      className="erp-grid__detail-drawer"
      footer={footer}
    >
      {!record || resolved.length === 0 ? (
        <Empty description="No details to show" />
      ) : (
        resolved.map((g) => (
          <section key={g.key} className="erp-grid__detail-group">
            <header className="erp-grid__detail-group-title">{g.title}</header>
            <Descriptions
              column={1}
              size="small"
              colon={false}
              className="erp-grid__detail-desc"
            >
              {g.items.map((it) => (
                <Descriptions.Item
                  key={it.key}
                  label={it.label}
                  span={it.span}
                >
                  {it.children}
                </Descriptions.Item>
              ))}
            </Descriptions>
          </section>
        ))
      )}
    </Drawer>
  );
}
